<template>
  <div class="message-bubble" :class="messageClass">
    <!-- AI消息 -->
    <div v-if="!isUser" class="ai-message">
      <div class="bubble ai-bubble clickable-bubble" @click="handleTextClick">
        <div class="message-content">
          <TypewriterText v-if="message.isTyping && message.content" :text="message.content" :speed="50"
            @complete="onTypingComplete" />
          <span v-else class="message-text">{{ message.content }}</span>
        </div>

        <!-- 音频播放按钮 -->
        <div v-if="message.audioUrl" class="audio-controls" @click.stop>
          <button class="audio-play-btn" :class="{ playing: isCurrentMessagePlaying }" @click="handleAudioClick"
            :title="getAudioButtonTitle()">
            <!-- 播放图标 -->
            <svg v-if="!isCurrentMessagePlaying" width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M8 5v14l11-7z" />
            </svg>
            <!-- 暂停图标 -->
            <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="white">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          </button>
          <span class="audio-status-text" :class="{ playing: isCurrentMessagePlaying }">
            {{ getAudioStatusText() }}
          </span>
        </div>
      </div>
    </div>

    <!-- 用户消息 -->
    <div v-else class="user-message">
      <div class="bubble user-bubble" :class="{ 'clickable-bubble': message.type === 'text' }"
        @click="message.type === 'text' ? handleTextClick() : null">
        <!-- 文字消息 -->
        <span v-if="message.type === 'text'" class="message-text">{{ message.content }}</span>
        <!-- 语音消息 -->
        <div v-else-if="message.audioData?.type === 'wechat'" class="voice-message-container" @click.stop="handleAudioClick">
          <div class="voice-wave-icon" :class="{ playing: isCurrentMessagePlaying }">
            <!-- 音频波形图标（5个竖条） -->
            <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <!-- 第1条：短 -->
              <rect class="wave-bar bar-1" x="2" y="14" width="3" height="6" rx="1.5" />
              <!-- 第2条：中 -->
              <rect class="wave-bar bar-2" x="8" y="10" width="3" height="10" rx="1.5" />
              <!-- 第3条：长 -->
              <rect class="wave-bar bar-3" x="14" y="4" width="3" height="16" rx="1.5" />
              <!-- 第4条：中 -->
              <rect class="wave-bar bar-4" x="20" y="10" width="3" height="10" rx="1.5" />
              <!-- 第5条：短 -->
              <rect class="wave-bar bar-5" x="26" y="14" width="3" height="6" rx="1.5" />
            </svg>
          </div>
        </div>
        <!-- 非微信语音消息（保留原样式） -->
        <div v-else class="voice-message-container">
          <div class="voice-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- 全屏文字显示 -->
    <FullscreenText v-model:show="showFullscreenText" :text="message.content" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { showToast } from 'vant'
import TypewriterText from '@/components/common/TypewriterText.vue'
import FullscreenText from '@/components/common/FullscreenText.vue'
import { useChatStore } from '@/stores/chat.js'
import { useAppStore } from '@/stores/app.js'
import { resumeAudioContext } from '@/composables/useAudio.js'

const props = defineProps(['message'])

const chatStore = useChatStore()
const appStore = useAppStore()

// 全屏文字显示状态
const showFullscreenText = ref(false)

// ==================== 计算属性 ====================

const isUser = computed(() => props.message.sender === 'user')

const messageClass = computed(() => ({
  'is-user': isUser.value,
  'is-ai': !isUser.value,
  'has-audio': !!props.message.audioUrl
}))

// 检查当前消息是否正在播放
const isCurrentMessagePlaying = computed(() => {
  if (props.message.audioUrl) {
    // AI消息：比较audioUrl
    return chatStore.isAudioPlaying && chatStore.currentAudioUrl === props.message.audioUrl
  } else if (props.message.audioData?.type === 'wechat') {
    // 用户微信录音：比较localId
    return chatStore.isAudioPlaying && chatStore.currentAudioUrl === props.message.audioData.localId
  }
  return false
})

// ==================== 工具方法 ====================

/**
 * 格式化时间
 */
const formatTime = (timestamp) => {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * 获取音频按钮提示
 */
const getAudioButtonTitle = () => {
  return isCurrentMessagePlaying.value ? '点击停止播放' : '点击播放语音'
}

/**
 * 获取音频状态文字
 */
const getAudioStatusText = () => {
  return isCurrentMessagePlaying.value ? '播放中...' : '播放'
}

// ==================== 事件处理 ====================

/**
 * 打字效果完成
 */
const onTypingComplete = () => {
  if (props.message.isTyping) {
    props.message.isTyping = false
  }
  console.log('AI消息打字完成')
}

/**
 * 处理音频点击
 */
const handleAudioClick = async () => {
  try {
    // 标记用户已交互
    appStore.markUserInteraction()

    // 尝试恢复音频上下文
    try {
      await resumeAudioContext()
    } catch (error) {
      console.log('恢复音频上下文失败:', error)
    }

    // AI消息播放
    if (props.message.audioUrl) {
      if (isCurrentMessagePlaying.value) {
        // 停止播放
        console.log('🎵 停止当前音频播放')
        chatStore.stopAudio()
      } else {
        // 开始播放
        console.log('🎵 播放AI音频')
        await chatStore.playAudio(props.message.audioUrl, 'ai', false)
      }
    }
    // 用户微信录音播放
    else if (props.message.audioData?.type === 'wechat') {
      if (isCurrentMessagePlaying.value) {
        // 停止播放
        console.log('🎵 停止微信录音播放')
        chatStore.stopAudio()
      } else {
        // 开始播放
        console.log('🎵 播放微信录音')
        await chatStore.playAudio(props.message.audioData.localId, 'user-wechat', false)
      }
    }
  } catch (error) {
    console.error('🎵 音频播放失败:', error)
    showToast({ type: 'fail', message: '音频播放失败，请重试' })
  }
}

/**
 * 处理文字点击 - 全屏显示
 */
const handleTextClick = () => {
  if (!props.message.content || props.message.content.trim() === '') {
    return
  }
  showFullscreenText.value = true
}
</script>

<style lang="scss" scoped>
.message-bubble {
  margin-bottom: 16px;
  display: flex;
  width: 100%;

  &.is-user {
    justify-content: flex-end;
  }

  &.is-ai {
    justify-content: flex-start;
  }
}

.ai-message {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 100%;
}

.user-message {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: 80%;
}

.bubble {
  padding: 16px 20px;
  border-radius: 20px;
  max-width: 100%;
  word-wrap: break-word;
  position: relative;
  line-height: 1.7;
  font-size: 20px;

  &.clickable-bubble {
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
      opacity: 0.9;
    }
  }

  &.ai-bubble {
    background: #ffffff;
    color: #333;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    gap: 12px;

    &::before {
      content: '';
      position: absolute;
      left: -6px;
      bottom: 12px;
      width: 0;
      height: 0;
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-right: 6px solid #ffffff;
    }

    &.clickable-bubble:hover {
      background: #f8f8f8;

      &::before {
        border-right-color: #f8f8f8;
      }
    }
  }

  &.user-bubble {
    background: #1989fa;
    color: white;

    &::before {
      content: '';
      position: absolute;
      right: -5px;
      bottom: 12px;
      width: 0;
      height: 0;
      border-top: 6px solid transparent;
      border-bottom: 6px solid transparent;
      border-left: 6px solid #1989fa;
    }

    &.clickable-bubble:hover {
      background: #1976d2;

      &::before {
        border-left-color: #1976d2;
      }
    }
  }
}

// 消息内容容器
.message-content {
  width: 100%;
}

// 音频控制按钮
.audio-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 8px;

  .audio-play-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #1989fa;
    border: none;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;

    &:hover {
      transform: scale(1.05);
    }

    &:active {
      transform: scale(0.95);
    }

    &.playing {
      background: #ff4757;

      &:hover {
        background: #ee3344;
      }
    }

    &:not(.playing) {
      animation: gentle-pulse 2s ease-in-out infinite;
    }
  }

  .audio-status-text {
    font-size: 16px;
    color: #1989fa;
    font-weight: 500;

    &.playing {
      color: #ff4757;
    }
  }
}

@keyframes gentle-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

// 语音消息样式
.voice-message-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  cursor: pointer;
  padding: 4px 0;
  transition: transform 0.2s;

  &:active {
    transform: scale(0.95);
  }
}

.voice-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 60px;

  svg {
    flex-shrink: 0;
  }
}

// 音频波形图标样式
.voice-wave-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  transition: all 0.3s ease;

  svg {
    .wave-bar {
      fill: white;
      transition: fill 0.3s ease, height 0.3s ease, y 0.3s ease;
    }
  }

  // 未播放状态：轻微脉冲动画
  &:not(.playing) {
    animation: gentle-pulse-white 2s ease-in-out infinite;

    svg {
      .wave-bar {
        fill: rgba(255, 255, 255, 0.9);
      }
    }
  }

  // 播放状态：图标变红色，波形跳动动画
  &.playing {
    svg {
      .wave-bar {
        fill: #ff4757;
        transform-origin: center bottom;
      }

      // 每个竖条不同的动画延迟和幅度，形成波浪效果
      .bar-1 {
        animation: wave-bounce-1 0.8s ease-in-out infinite;
        animation-delay: 0s;
      }

      .bar-2 {
        animation: wave-bounce-2 0.8s ease-in-out infinite;
        animation-delay: 0.15s;
      }

      .bar-3 {
        animation: wave-bounce-3 0.8s ease-in-out infinite;
        animation-delay: 0.3s;
      }

      .bar-4 {
        animation: wave-bounce-2 0.8s ease-in-out infinite;
        animation-delay: 0.15s;
      }

      .bar-5 {
        animation: wave-bounce-1 0.8s ease-in-out infinite;
        animation-delay: 0s;
      }
    }
  }

  &:hover {
    transform: scale(1.05);
  }
}

// 波形跳动动画 - 每个竖条不同的动画幅度
@keyframes wave-bounce {
  0%, 100% {
    transform: scaleY(1);
    opacity: 1;
  }
  50% {
    transform: scaleY(0.3);
    opacity: 0.8;
  }
}

// 为不同竖条创建不同的动画幅度
@keyframes wave-bounce-1 {
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(0.4);
  }
}

@keyframes wave-bounce-2 {
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(0.2);
  }
}

@keyframes wave-bounce-3 {
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(0.15);
  }
}

@keyframes gentle-pulse-white {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.9;
  }
}

// 移动端适配
@media (max-width: 480px) {
  .ai-message,
  .user-message {
    max-width: 100%;
  }

  .bubble {
    padding: 12px 16px;
    font-size: 18px;
  }

  .audio-controls {
    padding-top: 6px;
    gap: 6px;

    .audio-play-btn {
      width: 24px;
      height: 24px;

      svg {
        width: 10px;
        height: 10px;
      }
    }

    .audio-status-text {
      font-size: 14px;
    }
  }

  .voice-wave-icon {
    padding: 3px 6px;

    svg {
      width: 28px;
      height: 20px;
    }
  }

  .voice-message-container {
    padding: 2px 0;
  }

  .voice-message {
    svg {
      width: 18px;
      height: 18px;
    }
  }
}
</style>
